import { useMemo } from 'react';
import { textToColor } from 'helpers/textToColor/textToColor';
import { ExtendedProductListingProduct } from 'shared/types/product/Product';

export interface ColorOption {
  name: string;
  color: string;
  sku?: string;
}

/**
 * Custom hook to extract color options from a product
 * @param product The product to extract color options from
 * @returns An array of color options
 */
const useColorOptions = (product: ExtendedProductListingProduct): ColorOption[] => {
  return useMemo(() => {
    // Check if we have product level finishes
    if (product.finishes && Array.isArray(product.finishes) && product.finishes.length > 0) {
      return product.finishes
        .map((finish) => {
          if (!finish) return null;

          const colorInfo = textToColor(finish);
          if (!colorInfo.label || !colorInfo.code) return null;
          return {
            name: colorInfo.label,
            color: colorInfo.code,
          };
        })
        .filter(Boolean) as ColorOption[];
    }

    // Check if we have colorsMap
    if (product.colorsMap && Array.isArray(product.colorsMap) && product.colorsMap.length > 0) {
      return product.colorsMap
        .map((colorItem) => {
          const colorValue = colorItem.color;

          if (!colorValue) return null;

          const colorInfo = textToColor(colorValue);

          if (!colorInfo.label || !colorInfo.code) return null;

          const result: ColorOption = {
            name: colorInfo.label,
            color: colorInfo.code,
          };

          if (colorItem.sku) {
            result.sku = colorItem.sku;
          }

          return result;
        })
        .filter(Boolean) as ColorOption[];
    }

    return [];
  }, [product]);
};

export default useColorOptions;
